import { DateRange, DateRangePicker } from "@mui/x-date-pickers-pro";
import { useCallback } from "react";
import { convertDateToYYYYMMDDString } from "../../../../app/common";
import { Leave, LeaveEntityDateRange } from "../../entriesAPI";

// Debounce utility function
const debounce = (
  func: (...args: [key: string, value: any]) => void,
  waitFor: number,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: [key: string, value: any]) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func(...args), waitFor);
  };
};

interface Props {
  dateRange: LeaveEntityDateRange; // Now using the object structure
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
  updateLeaveEntity: (
    key: string,
    value: string | LeaveEntityDateRange | null | Leave,
  ) => void;
  disabled: boolean;
}

const LeaveEntityDateRangePicker = ({
  dateRange,
  inputErrors,
  setInputErrors,
  updateLeaveEntity,
  disabled,
}: Props) => {
  // Debounce updateLeaveEntity function using useCallback
  // FIXME: fix this or add comment why this is needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateLeaveEntity = useCallback(
    debounce((key: string, value: LeaveEntityDateRange) => {
      updateLeaveEntity(key, value);
    }, 500),
    [updateLeaveEntity],
  );

  // Function to handle when the date range is changed
  const handleDateRangeOnChange = (dateRange: DateRange<Date>) => {
    const [startDate, endDate] = dateRange;

    // If no start or end date, return early and set input errors
    if (!startDate || !endDate) {
      setInputErrors({
        ...inputErrors,
        dateRange: true,
      });
      return;
    }

    // Convert selected dates back to yyyy-MM-dd string format
    const formattedStartDate = convertDateToYYYYMMDDString(startDate, "-");
    const formattedEndDate = convertDateToYYYYMMDDString(endDate, "-");

    // If the date range has changed, update the input errors and trigger the update
    if (formattedStartDate && formattedEndDate) {
      setInputErrors({
        ...inputErrors,
        dateRange: false,
      });

      // Use the debounced function to update the leave entity with the new date range
      debouncedUpdateLeaveEntity("dateRange", {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }
  };

  // Helper function to create a Date object in local time from a string
  const createLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day); // Create a Date object with the provided year, month, and day
  };

  // Convert the initial string dates into Date objects for the DateRangePicker
  const convertedLeaveDateRange: DateRange<Date> = [
    dateRange.startDate ? createLocalDate(dateRange.startDate) : null,
    dateRange.endDate ? createLocalDate(dateRange.endDate) : null,
  ];

  return (
    <DateRangePicker
        disabled={disabled}
      format="dd.MM.yyyy"
      value={convertedLeaveDateRange || [null, null]} // Use the converted date range
      onChange={handleDateRangeOnChange}
      slotProps={{
        textField: {
          size: "small",
          label: null,
        },
        fieldSeparator: {
          children: "-",
          sx: { mx: 1 }, // Adjusts the margin between fields
        },
      }}
    />
  );
};

export default LeaveEntityDateRangePicker;
